<template>
  <div>
    <md-dialog :md-active.sync="showDialog" :md-click-outside-to-close="false">
      <md-dialog-title class="headingScale">
        <span class="material-icons" style="font-size:20px;vertical-align:middle;margin-right:6px;">scale</span>
        Scale Weight (Local USB)
      </md-dialog-title>

      <md-dialog-content>
        <callingApiCenter class="adjust" type="bar" :paths="['/api/w/fsi/WebSaveOutbound']"/>

        <h3 v-if="serialError" class="ErrorMessage">{{ serialError }}</h3>
        <h3 v-if="scaleNotConnectedError" class="ErrorMessage">Scale data not read. Please connect USB scale and read weight first!</h3>
        <h3 v-if="scaleTimeExpiredError" class="ErrorMessage">Scale data expired (over 60 seconds). Please read weight again!</h3>
        <h3 v-if="netWeightNegError" class="ErrorMessage">Total net weight cannot be less than 1!</h3>
        <h3 v-if="scaleWgtnummError" class="ErrorMessage">Total Weight is not a numeric!</h3>

        <!-- Load Info -->
        <div class="sectionLabel">Load Info</div>
        <div>
          <div class="layoutCell">Container#: <md-input :value="dataForm.containerNo" disabled/></div>
          <div class="layoutCell">Chassis#: <md-input :value="dataForm.chassisNo" disabled/></div>
          <div class="layoutCell">Type: <md-input :value="dataForm.ctnrType" disabled/></div>
        </div>

        <!-- USB Scale -->
        <div class="sectionLabel">USB Scale</div>
        <div style="display:flex;align-items:center;flex-wrap:wrap;margin-bottom:8px;">
          <label class="baudLabel">Baud Rate:</label>
          <select v-model="baudRate" class="baudSelect">
            <option v-for="rate in baudRateOptions" :key="rate" :value="rate">{{ rate }}</option>
          </select>
          <md-button class="md-raised md-primary md-dense" @click="readWeight" :disabled="reading" style="margin-left:10px;height:32px;">
            <span class="material-icons" style="font-size:15px;vertical-align:middle;margin-right:3px;">usb</span>
            {{ reading ? 'Reading...' : 'Read Weight' }}
          </md-button>
          <span v-if="serialStatus" class="serialStatus">{{ serialStatus }}</span>
        </div>

        <div>
          <div class="layoutCell">Scale Date: <md-input v-model="item.date" disabled/></div>
          <div class="layoutCell">Scale Time: <md-input v-model="item.time" disabled/></div>
          <div class="layoutCell">Weight (lb): <md-input v-model="item.nw" @keyup="chkWeightNumeric" disabled/></div>
        </div>

        <!-- Yard Goat -->
        <div class="sectionLabel">Yard Goat</div>
        <div class="layoutAutoComplete">
          <auto-complete-master-data-select
            :class="getValidationClass('dataForm.ygNo')"
            :preparedOptions="getYgOption()"
            label="Yard Goat"
            :init-value="dataForm.ygNo"
            :on-item-changed="handleYgChanged"
          />
        </div>

        <div class="layoutPlantWide">
          Yard Goat Weight: <strong>{{ dataForm.ygWeight != null ? dataForm.ygWeight : '—' }}</strong>
        </div>
        <div class="layoutPlantWide">
          <h3>Total Weight: {{ dataForm.weight != null ? dataForm.weight : '—' }}</h3>
        </div>

        <md-dialog-actions>
          <md-button class="md-raised custButton" @click="cancelAction">
            <span class="icons-center material-icons">cancel</span>Close
          </md-button>
          <md-button class="md-raised custButton md-primary" @click="checkAndSelect">
            <span class="icons-center material-icons">create</span>Select
          </md-button>
        </md-dialog-actions>

        <confirm-alert ref="confirmAlert"/>
      </md-dialog-content>
    </md-dialog>
  </div>
</template>

<style scoped>
form {
  width: 640px;
  margin: 0;
}

.headingScale {
  padding-top: 0;
  padding-left: 0;
  margin-bottom: 0;
  color: #3522af;
  line-height: 2;
  font-size: 22px;
  font-family: Roboto Slab;
  margin-left: 10px;
}

.sectionLabel {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #1565C0;
  border-left: 3px solid #1565C0;
  padding-left: 6px;
  margin-top: 10px;
  margin-bottom: 4px;
}

.layoutCell {
  padding: 0;
  width: 20%;
  line-height: 1.5;
  color: black;
  background-color: rgb(246, 226, 219);
  display: inline-block;
  font-family: Roboto Slab;
  margin: 2px;
}

.layoutPlantWide {
  padding-left: 7px;
  width: 240px;
  color: black;
  font-family: Roboto Slab;
  margin: 0;
}

.layoutPlantWide > h3 {
  margin-bottom: 0;
}

.layoutAutoComplete {
  width: 30%;
  padding-left: 5px;
  display: inline-block;
}

.baudLabel {
  font-size: 13px;
  margin-right: 6px;
}

.baudSelect {
  height: 28px;
  padding: 2px 6px;
  border: 1px solid #bbb;
  border-radius: 3px;
  font-size: 13px;
}

.serialStatus {
  font-size: 12px;
  color: #546E7A;
  margin-left: 8px;
}

.adjust {
  padding-top: 0;
  margin-top: 0;
}

.ErrorMessage {
  color: #f14668 !important;
  font-size: 13px;
}

.custButton {
  margin: 10px;
  width: 120px;
  height: 40px;
  line-height: 40px;
  font-size: 0.9rem;
}
</style>

<script>
import { validationMixin } from 'vuelidate';
import { mapGetters } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import callingApiCenter from './CallingApiCenter.vue';
import { newAPIPost } from '../api/index.js';
import moment from 'moment';
import confirmAlert from './ConfirmAlert.vue';

const getInitialDataForm = () => ({
  seqNo: null,
  ygNo: null,
  ygWeight: null,
  weight: null,
  driverLP: null,
  containerNo: null,
  chassisNo: null,
  ctnrType: null,
});

const getInitItem = () => ({
  date: moment().format('YYYY-MM-DD'),
  time: moment().format('HH:mm:ss'),
  nw: null,
});

export default {
  components: { callingApiCenter, confirmAlert },
  mixins: [validationMixin],

  props: {
    mode: String,
    action: Function,
  },

  data: () => ({
    showDialog: false,
    dataForm: getInitialDataForm(),
    item: getInitItem(),
    load: null,
    receivingWindows: '',
    netWeightNegError: false,
    scaleWgtnummError: false,
    baudRate: 9600,
    baudRateOptions: [1200, 2400, 4800, 9600, 19200, 38400, 57600, 115200],
    reading: false,
    serialError: null,
    serialStatus: null,
    scaleConnected: false,
    scaleNotConnectedError: false,
    scaleTimeExpiredError: false,
    ygMasterData: [],
  }),

  validations: {
    dataForm: {
      ygNo: { required },
    },
  },

  computed: {
    ...mapGetters([
      'getUserProfile',
      'getPermission',
    ]),
  },

  watch: {
    'dataForm.ygWeight'() { this.calcWeight(); },
    'item.nw'() { this.calcWeight(); },
  },

  methods: {
    async openDialog(existingUser) {
      this.$store.commit('clearApiError');
      this.$v.dataForm.$reset();
      this.dataForm = getInitialDataForm();
      this.item = getInitItem();
      this.load = existingUser;
      this.dataForm.containerNo = existingUser.containerNo;
      this.dataForm.chassisNo = existingUser.chassisNo;
      this.dataForm.ctnrType = existingUser.ctnrType;
      this.dataForm.driverLP = existingUser.driverLP;
      this.dataForm.seqNo = existingUser.seqNo;
      this.receivingWindows = existingUser.receivingWindows;
      if (this.mode === 'STR') {
        this.item.nw = existingUser.tareWeight ? parseInt(existingUser.tareWeight) : null;
      }
      if (this.mode === 'SWR') {
        this.item.nw = existingUser.vgmWeight ? parseInt(existingUser.vgmWeight) : null;
      }
      this.serialError = null;
      this.serialStatus = null;
      this.scaleConnected = false;
      this.scaleNotConnectedError = false;
      this.scaleTimeExpiredError = false;
      this.netWeightNegError = false;
      this.scaleWgtnummError = false;
      this.ygMasterData = [];
      const masterDatas = await newAPIPost('/api/w/masterData', { groups: ['YG_MASTER'] });
      if (Array.isArray(masterDatas)) {
        this.ygMasterData = masterDatas
          .filter(it => !!it.active)
          .map(it => ({
            id: it.tagId,
            name: it.tagName,
            weight: it.weight,
            recWindow: it.recWindow,
          }));
      }
      this.showDialog = true;
    },

    calcWeight() {
      if (this.mode === 'STR') {
        const gross = Number(this.load && this.load.vgmWeight ? this.load.vgmWeight : 0)
          - Number(this.load && this.load.gwYgWeight ? this.load.gwYgWeight : 0);
        const tare = Number(this.item.nw != null ? this.item.nw : 0)
          - Number(this.dataForm.ygWeight != null ? this.dataForm.ygWeight : 0);
        this.dataForm.weight = (gross - tare).toString();
      }
      if (this.mode === 'SWR') {
        const gross = Number(this.item.nw != null ? this.item.nw : 0)
          - Number(this.dataForm.ygWeight != null ? this.dataForm.ygWeight : 0);
        const tare = Number(this.load && this.load.tareWeight ? this.load.tareWeight : 0)
          - Number(this.load && this.load.twYgWeight ? this.load.twYgWeight : 0);
        this.dataForm.weight = (gross - tare).toString();
      }
    },

    async readWeight() {
      if (!navigator.serial) {
        this.serialError = 'Web Serial API not supported. Please use Chrome or Edge browser.';
        return;
      }
      this.reading = true;
      this.serialError = null;
      this.serialStatus = 'Connecting to port...';

      let port = null;
      try {
        port = await navigator.serial.requestPort();
        await port.open({
          baudRate: this.baudRate,
          dataBits: 8,
          stopBits: 1,
          parity: 'none',
        });

        this.serialStatus = 'Reading data...';
        const reader = port.readable.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        try {
          const deadline = Date.now() + 5000;
          while (Date.now() < deadline) {
            const readPromise = reader.read();
            const timeoutPromise = new Promise((_, rej) =>
              setTimeout(() => rej(new Error('timeout')), 5000)
            );
            const { value, done } = await Promise.race([readPromise, timeoutPromise]);
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            if (buffer.includes('\n') || buffer.includes('\r')) break;
          }
        } finally {
          reader.releaseLock();
        }

        await port.close();
        this.serialStatus = null;
        this.parseSerialData(buffer.trim());

      } catch (err) {
        if (err.name === 'NotFoundError') {
          this.serialError = null;
        } else if (err.message === 'timeout') {
          this.serialError = 'Timeout: no data received from scale within 5 seconds.';
        } else {
          this.serialError = 'Error reading scale: ' + err.message;
        }
        this.serialStatus = null;
        if (port) {
          try { await port.close(); } catch (_) { /* ignore */ }
        }
      } finally {
        this.reading = false;
      }
    },

    parseSerialData(raw) {
      const numbers = raw.match(/\d+/g);
      if (numbers && numbers.length > 0) {
        const weight = Math.max(...numbers.map(n => parseInt(n)));
        this.item.nw = weight;
        this.item.date = moment().format('YYYY-MM-DD');
        this.item.time = moment().format('HH:mm:ss');
        this.scaleConnected = true;
        this.scaleNotConnectedError = false;
        this.scaleTimeExpiredError = false;
      } else {
        this.serialError = 'Could not parse weight from received data: ' + JSON.stringify(raw);
      }
    },

    chkWeightNumeric() {
      this.scaleWgtnummError = this.item.nw != null && !/^\d+$/.test(String(this.item.nw));
    },

    handleYgChanged(id, _tag, _tag1, weight) {
      this.dataForm.ygNo = id;
      this.dataForm.ygWeight = weight;
    },

    getYgOption() {
      if (this.receivingWindows == '185' || this.receivingWindows == '187' || this.receivingWindows == '888') {
        return this.ygMasterData.filter(it => it.recWindow == '185');
      }
      return this.ygMasterData.filter(it => it.recWindow == '186');
    },

    getValidationClass(fieldName) {
      const field = _(this.$v).lookup(fieldName);
      if (field) {
        return { 'md-invalid': field.$invalid && field.$dirty };
      }
      return {};
    },

    cancelAction() {
      this.showDialog = false;
      this.$store.commit('clearApiError');
    },

    async checkAndSelect() {
      this.scaleNotConnectedError = false;
      this.scaleTimeExpiredError = false;

      if (!this.scaleConnected) {
        this.scaleNotConnectedError = true;
        return;
      }

      const diffSeconds = moment().diff(moment(this.item.date + 'T' + this.item.time), 'seconds');
      if (diffSeconds > 60) {
        this.scaleTimeExpiredError = true;
        return;
      }

      if (this.scaleWgtnummError) {
        alert('Total Weight is not a numeric!');
        return;
      }
      await this.doSaveUser();
    },

    async doSaveUser() {
      this.$v.dataForm.$touch();
      if (this.$v.dataForm.$invalid) return;
      if (this.item.nw == null || this.item.nw === '') return;

      const scaleDateTime = this.item.date + 'T' + this.item.time;

      if (this.mode === 'STR') {
        const seqNo = this.load.seqNo;
        await newAPIPost('/api/w/fsi/WebSaveOutbound', {
          mode: 'OTW',
          user: this.load.user,
          username: this.getUserProfile.username,
          seqNo,
          twDate: scaleDateTime,
          twDateWeb: scaleDateTime,
          tareWeight: this.item.nw,
          twYgNo: this.dataForm.ygNo,
          twYgWeight: this.dataForm.ygWeight,
          weight: this.dataForm.weight,
          twStation: 'LOCAL',
          driverLP: this.dataForm.driverLP,
          containerNo: this.dataForm.containerNo,
          chassisNo: this.dataForm.chassisNo,
          ctnrType: this.dataForm.ctnrType,
        }).then((ticket) => {
          this.showDialog = false;
          this.$store.commit('clearApiError');
          this.action(seqNo, ticket, this.mode);
        });
      }

      if (this.mode === 'SWR') {
        this.netWeightNegError = false;
        if (this.dataForm.weight < 1) {
          this.netWeightNegError = true;
          return;
        }
        const seqNo = this.load.seqNo;
        await newAPIPost('/api/w/fsi/WebSaveOutbound', {
          mode: 'OGW',
          user: this.load.user,
          username: this.getUserProfile.username,
          seqNo,
          gwDate: scaleDateTime,
          gwDateWeb: scaleDateTime,
          vgmWeight: this.item.nw,
          gwYgNo: this.dataForm.ygNo,
          gwYgWeight: this.dataForm.ygWeight,
          gwStation: 'LOCAL',
          weight: this.dataForm.weight,
        }).then((ticket) => {
          this.showDialog = false;
          this.$store.commit('clearApiError');
          this.action(seqNo, ticket, this.mode);
        });
      }
    },
  },
};
</script>
